/**
 * Pure presentation helpers for the conversations feature, copied from the
 * prototype engine (`data/posted.ts` `postedLabel`, `data/postBreakdown.ts`
 * `breakdownSummary`) so presenters need not import the mock engine.
 */
import type { PostBreakdown, SentimentKey } from '@/data-sources/contract/conversations';

import { compactNumber } from './metrics';

/**
 * A comment or a reply rather than a post: v2's `comment` and `reply`, and the
 * sample data's `comment`, `reply` and `*-comment`. It answers a post, so it
 * has no thread of its own to break down, and it is not counted as a post.
 */
export const isCommentOrReply = (type: string) => /comment|^repl/i.test(type);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * `Mon Sep 14, 9:42 PM` — how every table writes a specific moment. No year:
 * every window the app shows sits inside one year, and the weekday is the part
 * a reader uses. Composed by hand: `Intl` puts a comma after the weekday.
 */
export function postedLabel(epoch: number): string {
  const d = new Date(epoch);
  return `${WEEKDAYS[d.getDay()]} ${postedLabelCompact(epoch)}`;
}

/**
 * `Sep 14, 9:42 PM` — `postedLabel` without the weekday, for a table column
 * that has to show the whole moment in about 100px (the conversations list).
 */
export function postedLabelCompact(epoch: number): string {
  const d = new Date(epoch);
  const hour24 = d.getHours();
  /* Midnight and noon are 12, not 0. */
  const hour = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const minute = String(d.getMinutes()).padStart(2, '0');
  const period = hour24 < 12 ? 'AM' : 'PM';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${hour}:${minute} ${period}`;
}

export const BREAKDOWN_SENTIMENTS: readonly SentimentKey[] = ['positive', 'neutral', 'negative'];

/** `likes + reactions` — what the breakdown's reaction types add up to. */
export const totalReactionsOf = (b: PostBreakdown) =>
  b.reactions.reduce((sum, r) => sum + r.count, 0);

/**
 * The paragraph the breakdown panels' `AI summary` card prints. Figures only,
 * never causes — this can count comments and reactions, not know why they were
 * written. A template over figures already on screen, stated flatly.
 */
/**
 * The comments the sentiment split covers — what its shares are of. The
 * contract makes it `relevantComments`; deepdive-v2 classifies every comment,
 * so on live data it is the split's own sum, and shares never pass 100%.
 */
export function sentimentBase(b: PostBreakdown): number {
  return BREAKDOWN_SENTIMENTS.reduce((sum, s) => sum + b.commentSentiment[s], 0);
}

export function breakdownSummary(
  b: PostBreakdown,
  sentimentLabel: (sentiment: SentimentKey) => string,
  noun: 'post' | 'conversation' = 'post',
): string {
  const n = compactNumber.format.bind(compactNumber);
  const totalReactions = totalReactionsOf(b);

  if (b.relevantComments === 0) {
    return `No relevant comments on this ${noun} yet, so there is nothing to break down by sentiment. It carries **${n(totalReactions)} reactions** and **${n(b.totalShares)} shares**.`;
  }

  const base = sentimentBase(b);
  const share = (s: SentimentKey) => (base === 0 ? 0 : (b.commentSentiment[s] / base) * 100);
  const leading = BREAKDOWN_SENTIMENTS.reduce((best, s) =>
    b.commentSentiment[s] > b.commentSentiment[best] ? s : best,
  );
  const net = share('positive') - share('negative');
  const top = [...b.reactions].sort((x, y) => y.count - x.count)[0];
  const relevance = (b.relevantComments / b.totalComments) * 100;

  return [
    `**${n(b.relevantComments)} of ${n(b.totalComments)} comments** on this ${noun} are about its subject — ${relevance.toFixed(0)}% of them.`,
    `**${sentimentLabel(leading)}** leads at ${share(leading).toFixed(1)}%, putting net sentiment at **${net > 0 ? '+' : ''}${net.toFixed(1)}**.`,
    top && totalReactions > 0
      ? `Of ${n(totalReactions)} reactions, **${top.label}** is the most common.`
      : `It has drawn no reactions.`,
    `The ${noun} has been shared **${n(b.totalShares)}** times.`,
  ].join(' ');
}
